import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  message?: string | undefined;
  invalid?: boolean;
  showLabel: string;
  hideLabel: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  { label, message, invalid = false, showLabel, hideLabel, required, className, ...rest },
  ref,
) {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  const [visible, setVisible] = useState(false);

  return (
    <div className="zs-field">
      <label className="zs-field__label" htmlFor={inputId}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <div className="md-password-field__row">
        <input
          id={inputId}
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={['zs-field__input', className].filter(Boolean).join(' ')}
          aria-invalid={invalid || undefined}
          aria-describedby={message ? messageId : undefined}
          aria-required={required || undefined}
          required={required}
          {...rest}
        />
        <button
          type="button"
          className="md-password-field__toggle"
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          onClick={() => setVisible((previous) => !previous)}
        >
          {visible ? <EyeSlashIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
        </button>
      </div>
      {message ? (
        <span
          id={messageId}
          className={invalid ? 'zs-field__error' : 'zs-field__label'}
          role={invalid ? 'alert' : undefined}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
});
